import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { maps, Lineup } from '../data/maps';
import LineupCard from './LineupCard';

interface ModalProps {
  lineup: Lineup | null;
  isOpen: boolean;
  onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({ lineup, isOpen, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Get all media including images and video
  const allMedia = lineup ? [...lineup.images, ...(lineup.video ? [lineup.video] : [])] : [];
  const currentMedia = allMedia[currentImageIndex];
  const hasMultipleMedia = allMedia.length > 1;
  const isCurrentMediaVideo = lineup?.video && currentMedia === lineup.video;

  // Reset image index when lineup changes
  useEffect(() => {
    if (isOpen && lineup) {
      setCurrentImageIndex(0);
    }
  }, [isOpen, lineup]);

  if (!isOpen || !lineup) return null;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 1));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      // Add pan functionality here if needed
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMediaChange = (direction: 'next' | 'prev') => {
    if (!hasMultipleMedia) return;
    
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % allMedia.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
    }
    setZoom(1); // Reset zoom when changing media
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gray-800 rounded-lg w-full h-full sm:max-w-7xl sm:max-h-full overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-6">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-white truncate flex-1 mr-2">{lineup.title}</h2>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Zoom Controls - Hidden on mobile */}
              <div className="hidden sm:flex items-center gap-2 mr-4">
                <button
                  onClick={handleZoomOut}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm font-medium"
                  disabled={zoom <= 1}
                >
                  -
                </button>
                <span className="text-white text-sm min-w-[3rem] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm font-medium"
                  disabled={zoom >= 3}
                >
                  +
                </button>
                <button
                  onClick={handleResetZoom}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm font-medium"
                >
                  Reset
                </button>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-xl sm:text-2xl font-bold p-1"
              >
                ×
              </button>
            </div>
          </div>
          <div 
            className="bg-gray-700 rounded-lg p-2 sm:p-4 flex items-center justify-center overflow-hidden"
            style={{ 
              height: 'calc(100vh - 200px)',
              cursor: zoom > 1 ? 'grab' : 'default'
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {isCurrentMediaVideo ? (
                <video 
                  src={currentMedia}
                  className="object-contain rounded-lg shadow-lg transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom})`,
                    maxWidth: zoom > 1 ? 'none' : '100%',
                    maxHeight: zoom > 1 ? 'none' : '100%',
                    transformOrigin: 'center center'
                  }}
                  controls
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <img 
                  src={currentMedia} 
                  alt={`${lineup.title} - Media ${currentImageIndex + 1}`}
                  className="object-contain rounded-lg shadow-lg transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom})`,
                    maxWidth: zoom > 1 ? 'none' : '100%',
                    maxHeight: zoom > 1 ? 'none' : '100%',
                    transformOrigin: 'center center'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              )}
              
              {/* Media Navigation Controls */}
              {hasMultipleMedia && (
                <>
                  <button
                    onClick={handleMediaChange.bind(null, 'prev')}
                    className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 sm:p-3 transition-all duration-200 hover:scale-110 z-10"
                  >
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleMediaChange.bind(null, 'next')}
                    className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 sm:p-3 transition-all duration-200 hover:scale-110 z-10"
                  >
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex gap-1 sm:gap-2 z-10">
                    {allMedia.map((_, index: number) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentImageIndex(index);
                          setZoom(1);
                        }}
                        className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ${
                          index === currentImageIndex 
                            ? 'bg-yellow-400' 
                            : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="hidden text-center">
              <div className="w-32 h-32 bg-gray-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-4xl">📸</span>
              </div>
              <p className="text-gray-300 text-lg">Image not found</p>
              <p className="text-gray-400 text-sm mt-2">
                {lineup.images[0]}
              </p>
            </div>
          </div>
          {lineup.description && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-700 rounded-lg border-l-4 border-green-400">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-green-400 text-xs sm:text-sm font-medium">Description:</span>
                <span className="text-gray-300 text-sm sm:text-base">{lineup.description}</span>
              </div>
            </div>
          )}
          {lineup.credit && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-700 rounded-lg border-l-4 border-yellow-400">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-yellow-400 text-xs sm:text-sm font-medium">Credit:</span>
                <span className="text-gray-300 text-sm sm:text-base">{lineup.credit}</span>
                {lineup.socialLinks && (
                  <div className="flex items-center gap-2 mt-1 sm:mt-0 sm:ml-2">
                    {lineup.socialLinks.youtube && (
                      <a
                        href={lineup.socialLinks.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-500 hover:text-red-400 transition-colors"
                        title="YouTube"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </a>
                    )}
                    {lineup.socialLinks.twitch && (
                      <a
                        href={lineup.socialLinks.twitch}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-500 hover:text-purple-400 transition-colors"
                        title="Twitch"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                        </svg>
                      </a>
                    )}
                    {lineup.socialLinks.x && (
                      <a
                        href={lineup.socialLinks.x}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-300 transition-colors"
                        title="X"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </a>
                    )}
                    {lineup.socialLinks.instagram && (
                      <a
                        href={lineup.socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-500 hover:text-pink-400 transition-colors"
                        title="Instagram"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </a>
                    )}
                    {lineup.socialLinks.steam && (
                      <a
                        href={lineup.socialLinks.steam}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-300 transition-colors"
                        title="Steam"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.63 4.13 10.3 9.5 11.1L10.5 22c0 .55.45 1 1 1s1-.45 1-1v-1.1C17.87 22.3 22 17.63 22 12 22 5.37 16.63 0 12 0zM7.5 16.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5-4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5 4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LineupsPage: React.FC = () => {
  const { mapName, side } = useParams<{ mapName: string; side: string }>();
  const [selectedLineup, setSelectedLineup] = useState<Lineup | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const map = maps[mapName || ''];
  const lineups = side === 'CT' ? map?.CT : map?.T;

  if (!map || !lineups) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-red-400 mb-4">Page not found</h1>
        <Link to="/" className="text-yellow-400 hover:text-yellow-300">
          Return to home
        </Link>
      </div>
    );
  }

  const sideName = side === 'CT' ? 'Counter-Terrorist' : 'Terrorist';
  const sideColor = side === 'CT' ? 'blue' : 'orange';

  const openModal = (lineup: Lineup) => {
    setSelectedLineup(lineup);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLineup(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-yellow-400 mb-2">{map.displayName}</h1>
        <h2 className={`text-2xl font-semibold text-${sideColor}-400 mb-4`}>{sideName} Lineups</h2>
        <p className="text-gray-300 text-lg">{lineups.length} lineups available</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {lineups.map((lineup, index) => (
          <LineupCard
            key={index}
            lineup={lineup}
            onClick={() => openModal(lineup)}
          />
        ))}
      </div>

      <Modal
        lineup={selectedLineup}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default LineupsPage; 